import React, { useState } from 'react';
import {
    RefreshCw,
    Play,
    Target,
    Download
} from 'lucide-react';

/**
 * Batch Controls Component
 * Handles batch validation controls, progress display, and store selection
 * @param {Object} props - Component props
 * @param {Object} props.batchValidation - Batch validation state object
 * @param {Array} props.selectedStores - Array of selected store IDs
 * @param {Array} props.filteredStores - Array of filtered stores
 * @param {Object} props.validationResults - Array of validation results
 * @param {Object} props.callbacks - Callback functions
 * @returns {JSX.Element} Batch controls interface
 */
export function BatchControls({
    batchValidation = {},
    selectedStores = [],
    filteredStores = [],
    validationResults = [],
    callbacks = {}
}) {
    const {
        runBatchValidation = () => { },
        setSelectedStores = () => { },
        filterStatus = 'all',
        setFilterStatus = () => { }
    } = callbacks;

    const [localSelectedStores, setLocalSelectedStores] = useState(selectedStores);

    const handleStoreSelection = (storeId, isSelected) => {
        const updatedSelection = isSelected
            ? [...localSelectedStores, storeId]
            : localSelectedStores.filter(id => id !== storeId);

        setLocalSelectedStores(updatedSelection);
        setSelectedStores(updatedSelection);
    };

    const handleSelectAllFiltered = () => {
        const allIds = filteredStores.map(s => s._id);
        setLocalSelectedStores(allIds);
        setSelectedStores(allIds);
    };

    const handleRunBatchValidation = () => {
        runBatchValidation(localSelectedStores);
    };

    const handleRunAllFiltered = () => {
        const filteredIds = filteredStores.map(s => s._id);
        runBatchValidation(filteredIds);
    };

    const handleExportResults = () => {
        // Export functionality would go here
        console.log('Export validation results');
    };

    return (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-slate-900">Batch Validation</h3>
                <div className="flex items-center gap-3">
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
                    >
                        <option value="all">All Stores</option>
                        <option value="excellent">Excellent (90%+)</option>
                        <option value="good">Good (70-89%)</option>
                        <option value="warning">Warning (50-69%)</option>
                        <option value="critical">Critical (Under 50%)</option>
                        <option value="not_validated">Not Validated</option>
                    </select>
                    <button
                        onClick={handleSelectAllFiltered}
                        className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50"
                    >
                        Select All Filtered
                    </button>
                </div>
            </div>

            {batchValidation.isRunning && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <RefreshCw size={16} className="text-blue-600 animate-spin" />
                            <span className="font-medium text-blue-900">Validating Stores...</span>
                        </div>
                        <span className="text-sm text-blue-700">{batchValidation.progress || 0}%</span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
                        <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${batchValidation.progress || 0}%` }}
                        />
                    </div>
                    {batchValidation.currentStore && (
                        <div className="text-sm text-blue-700">
                            Currently validating: {batchValidation.currentStore}
                        </div>
                    )}
                </div>
            )}

            <div className="flex items-center gap-4 mb-6">
                <button
                    onClick={handleRunBatchValidation}
                    disabled={batchValidation.isRunning || localSelectedStores.length === 0}
                    className={`
            flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all
            ${batchValidation.isRunning || localSelectedStores.length === 0
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-orange-500 text-white hover:bg-orange-600'
                        }
          `}
                >
                    {batchValidation.isRunning ? (
                        <>
                            <RefreshCw size={16} className="animate-spin" />
                            Validating...
                        </>
                    ) : (
                        <>
                            <Play size={16} />
                            Validate Selected ({localSelectedStores.length})
                        </>
                    )}
                </button>

                <button
                    onClick={handleRunAllFiltered}
                    disabled={batchValidation.isRunning || filteredStores.length === 0}
                    className={`
            flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all border
            ${batchValidation.isRunning || filteredStores.length === 0
                            ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                            : 'border-orange-500 text-orange-600 hover:bg-orange-50'
                        }
          `}
                >
                    <Target size={16} />
                    Validate All Filtered ({filteredStores.length})
                </button>

                {validationResults.length > 0 && (
                    <button
                        className="flex items-center gap-2 px-4 py-3 rounded-lg font-medium border border-slate-300 text-slate-600 hover:bg-slate-50"
                        onClick={handleExportResults}
                    >
                        <Download size={16} />
                        Export Results
                    </button>
                )}
            </div>

            {/* Store Selection */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredStores.map(store => {
                    const isSelected = localSelectedStores.includes(store._id);
                    const result = validationResults.find(r => r.storeId === store._id);

                    return (
                        <div
                            key={store._id}
                            className={`
                flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all
                ${isSelected ? 'border-orange-300 bg-orange-50' : 'border-slate-200 hover:border-slate-300'}
              `}
                            onClick={() => handleStoreSelection(store._id, !isSelected)}
                        >
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => { }}
                                    className="w-4 h-4 text-orange-600 rounded"
                                />
                                <div>
                                    <div className="font-medium text-slate-900">{store.name}</div>
                                    <div className="text-sm text-slate-500">
                                        {store.region} • {store.pages?.length || 0} pages
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {result && (
                                    <div className={`
                    px-2 py-1 rounded text-xs font-medium
                    ${result.overallScore >= 80 ? 'bg-emerald-100 text-emerald-700' :
                                            result.overallScore >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}
                  `}>
                                        {result.overallScore}%
                                    </div>
                                )}
                                <div className="text-sm text-slate-400">
                                    {result ? new Date(result.timestamp || Date.now()).toLocaleDateString() : 'Not validated'}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default BatchControls;
